import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Play, 
  FileText, 
  Download, 
  Lock, 
  Check, 
  ChevronDown, 
  ChevronRight, 
  BookOpen, 
  Clock,
  Award,
  Loader2,
  X,
  Library
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Video Player Modal Component
const VideoPlayerModal = ({ lesson, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-5xl">
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-stone-300 flex items-center gap-2"
          data-testid="close-video-btn"
        >
          <X className="w-6 h-6" />
          Close
        </button>
        
        {lesson.resource_id ? (
          <video
            src={`${BACKEND_URL}/api/resources/stream/${lesson.resource_id}`}
            controls
            autoPlay
            crossOrigin="use-credentials"
            className="w-full rounded-lg shadow-2xl"
            data-testid="lesson-video-player"
          >
            Your browser does not support video playback.
          </video>
        ) : (
          <div className="bg-lws-green/10 rounded-lg p-12 text-center">
            <Play className="w-16 h-16 text-lws-green mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-serif text-white mb-2">{lesson.title}</h3>
            <p className="text-white/70 mb-4">
              Video content coming soon. Duration: {lesson.duration}
            </p>
            <p className="text-sm text-white/50">
              Admin: Upload video via Admin Resources panel and link it to this lesson.
            </p>
          </div>
        )}
        
        <div className="mt-4 text-center">
          <h3 className="text-white font-serif text-lg">{lesson.title}</h3>
          {lesson.duration && (
            <p className="text-white/60 text-sm mt-1 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              {lesson.duration}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Downloadable Resource Modal
const ResourceModal = ({ lesson, onClose }) => {
  const handleDownload = () => {
    if (lesson.resource_id) {
      window.open(`${BACKEND_URL}/api/resources/download/${lesson.resource_id}`, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-serif text-lws-ink">{lesson.title}</h3>
          <button onClick={onClose} className="text-lws-taupe hover:text-lws-ink">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-6">
          <div className="w-16 h-16 bg-lws-green/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-lws-green" />
          </div>
          <p className="text-center text-lws-taupe">
            {lesson.type === 'guide' && 'Download this helpful guide to support your letter writing journey.'}
            {lesson.type === 'template' && 'Download this template to use in your correspondence practice.'}
            {lesson.type === 'article' && 'Read this article to deepen your understanding.'}
          </p>
        </div>

        {lesson.resource_id ? (
          <button
            onClick={handleDownload}
            className="w-full bg-lws-green text-white py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-lws-green/90 transition-colors"
            data-testid="download-resource-btn"
          >
            <Download className="w-5 h-5" />
            Download Resource
          </button>
        ) : (
          <div className="text-center py-4 bg-lws-amber/10 rounded-lg">
            <p className="text-lws-amber text-sm">
              Resource coming soon. Check back later or visit the{' '}
              <a href="#resources" className="underline">Resource Library</a>.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

const LessonIcon = ({ type }) => {
  switch (type) {
    case "video":
      return <Play className="w-4 h-4" />;
    case "article":
      return <FileText className="w-4 h-4" />;
    case "guide":
    case "template":
      return <Download className="w-4 h-4" />;
    case "certificate":
      return <Award className="w-4 h-4" />;
    default:
      return <FileText className="w-4 h-4" />;
  }
};

const ModuleAccordion = ({ module, isOpen, onToggle, onLessonComplete, completedLessons, onLessonClick }) => {
  const completedInModule = module.lessons.filter(l => completedLessons.includes(l.id)).length;
  const progress = (completedInModule / module.lessons.length) * 100;

  return (
    <div 
      className={`border ${module.has_access ? 'border-lws-ink/10' : 'border-lws-ink/5 bg-lws-paper/50'}`}
      data-testid={`module-${module.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between text-left"
        data-testid={`module-toggle-${module.id}`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            module.has_access 
              ? progress === 100 
                ? 'bg-lws-green text-lws-paper' 
                : 'bg-lws-green/10 text-lws-green'
              : 'bg-lws-ink/5 text-lws-taupe'
          }`}>
            {progress === 100 ? (
              <Check className="w-5 h-5" />
            ) : module.has_access ? (
              <span className="font-serif">{module.number}</span>
            ) : (
              <Lock className="w-4 h-4" />
            )}
          </div>
          <div>
            <h3 className={`text-lg font-serif ${module.has_access ? 'text-lws-ink' : 'text-lws-taupe'}`}>
              {module.title}
            </h3>
            <p className="text-sm text-lws-taupe mt-1">
              {module.lesson_count} lessons
              {module.has_access && completedInModule > 0 && (
                <span className="ml-2 text-lws-green">
                  • {completedInModule}/{module.lesson_count} completed
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {!module.has_access && (
            <span className="text-xs uppercase tracking-wider text-lws-amber bg-lws-amber/10 px-3 py-1">
              {module.tier_required === "community" || module.tier_required === "pen_friend" ? "Community" : "Course"} Required
            </span>
          )}
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-lws-taupe" />
          ) : (
            <ChevronRight className="w-5 h-5 text-lws-taupe" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 border-t border-lws-ink/5">
              <p className="text-lws-taupe py-4">{module.description}</p>
              
              {/* Progress bar */}
              {module.has_access && (
                <div className="mb-4">
                  <div className="h-1 bg-lws-ink/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-lws-green transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {module.lessons.map((lesson) => {
                  const isCompleted = completedLessons.includes(lesson.id);
                  const isLocked = lesson.locked;
                  const isClickable = !isLocked && (lesson.type === 'video' || lesson.downloadable);
                  
                  return (
                    <div
                      key={lesson.id}
                      onClick={() => isClickable && onLessonClick(lesson)}
                      className={`flex items-center justify-between p-4 rounded ${
                        isLocked 
                          ? 'bg-lws-ink/5 opacity-60' 
                          : isCompleted 
                            ? 'bg-lws-green/5 border border-lws-green/20' 
                            : 'bg-white border border-lws-ink/10 hover:border-lws-green/30'
                      } ${isClickable ? 'cursor-pointer hover:shadow-sm' : ''}`}
                      data-testid={`lesson-${lesson.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isLocked 
                            ? 'bg-lws-ink/10 text-lws-taupe' 
                            : isCompleted 
                              ? 'bg-lws-green text-lws-paper' 
                              : 'bg-lws-green/10 text-lws-green'
                        }`}>
                          {isLocked ? (
                            <Lock className="w-4 h-4" />
                          ) : isCompleted ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <LessonIcon type={lesson.type} />
                          )}
                        </div>
                        <div>
                          <p className={`font-medium ${isLocked ? 'text-lws-taupe' : 'text-lws-ink'}`}>
                            {lesson.title}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-lws-taupe mt-1">
                            <span className="capitalize">{lesson.type}</span>
                            {lesson.duration && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {lesson.duration}
                                </span>
                              </>
                            )}
                            {lesson.downloadable && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Download className="w-3 h-3" />
                                  Downloadable
                                </span>
                              </>
                            )}
                            {!isLocked && lesson.type === 'video' && (
                              <span className="text-lws-green font-medium">Click to watch</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {!isLocked && !isCompleted && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onLessonComplete(lesson.id);
                          }}
                          className="text-xs uppercase tracking-wider text-lws-green hover:text-lws-amber transition-colors"
                          data-testid={`complete-btn-${lesson.id}`}
                        >
                          Mark Complete
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CoursesPage = ({ onMount }) => {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [userTier, setUserTier] = useState("free");
  const [completedLessons, setCompletedLessons] = useState([]);
  const [openModule, setOpenModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overallProgress, setOverallProgress] = useState(0);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [courseResources, setCourseResources] = useState([]);

  useEffect(() => {
    fetchModules();
    fetchCourseResources();
  }, [user]);

  // Trigger onMount callback for onboarding tour
  useEffect(() => {
    if (onMount && user && modules.length > 0) {
      // Small delay to let DOM settle
      const timer = setTimeout(() => onMount(), 500);
      return () => clearTimeout(timer);
    }
  }, [onMount, user, modules]);

  const fetchCourseResources = async () => {
    // Fetch resources that belong to course modules
    try {
      if (user) {
        const res = await axios.get(`${BACKEND_URL}/api/resources/list?category=course_module`, {
          withCredentials: true
        });
        setCourseResources(res.data.resources || []);
      }
    } catch (err) {
      console.log('Course resources not yet available');
    }
  };

  // Map resources to lessons by module number
  const getLessonResource = (lesson, moduleNumber) => {
    // Look for a resource that matches this lesson's module and type
    const resource = courseResources.find(r => 
      r.module_number === moduleNumber && 
      r.title.toLowerCase().includes(lesson.title.toLowerCase().split(' ')[0])
    );
    return resource?.id || null;
  };

  const handleLessonClick = (lesson, moduleNumber) => {
    const resourceId = getLessonResource(lesson, moduleNumber);
    setSelectedLesson({ ...lesson, resource_id: resourceId });
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/courses/modules`, {
        withCredentials: true
      });
      setModules(response.data.modules);
      setUserTier(response.data.user_tier);
      
      // Get user's progress if logged in
      if (user) {
        const statusRes = await axios.get(`${BACKEND_URL}/api/membership/status`, {
          withCredentials: true
        });
        if (statusRes.data.course_progress) {
          setCompletedLessons(statusRes.data.course_progress.completed_lessons || []);
          setOverallProgress(statusRes.data.course_progress.completion_percentage || 0);
        }
      }
      
      // Auto-open first accessible module
      const firstAccessible = response.data.modules.find(m => m.has_access);
      if (firstAccessible) {
        setOpenModule(firstAccessible.id);
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLessonComplete = async (lessonId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/courses/progress/${lessonId}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setCompletedLessons(prev => [...prev, lessonId]);
        setOverallProgress(response.data.completion_percentage);
        
        if (response.data.course_completed) {
          // Show celebration or redirect
          alert("Congratulations! You've completed The Correspondence Method! You can now join as a Society Member.");
        }
      }
    } catch (error) {
      console.error("Error marking lesson complete:", error);
      if (error.response?.status === 401) {
        window.location.hash = "#login";
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lws-green" />
      </div>
    );
  }

  const totalLessons = modules.reduce((sum, m) => sum + m.lesson_count, 0);
  const accessibleModules = modules.filter(m => m.has_access).length;

  return (
    <div className="min-h-screen" data-testid="courses-page">
      {/* Hero */}
      <section className="py-24 px-6 bg-lws-green text-lws-paper">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.4em] text-lws-amber mb-6 block"
          >
            The Curriculum
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-serif mb-6"
          >
            The Correspondence <span className="italic text-lws-amber">Method</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-lws-paper/80 max-w-2xl mx-auto"
          >
            Eight modules to transform you from blank-page anxiety to confident correspondent.
          </motion.p>
        </div>
      </section>

      {/* Progress Overview */}
      {user && userTier !== "free" && (
        <section className="py-8 px-6 bg-lws-paper border-b border-lws-ink/10">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <BookOpen className="w-5 h-5 text-lws-green" />
                <span className="text-lws-ink font-medium">Your Progress</span>
              </div>
              <span className="text-lws-green font-serif text-lg">
                {overallProgress.toFixed(0)}% Complete
              </span>
            </div>
            <div className="h-2 bg-lws-ink/5 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full bg-lws-green"
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-lws-taupe">
              <span>{completedLessons.length} of {totalLessons} lessons completed</span>
              <span>{accessibleModules} of {modules.length} modules accessible</span>
            </div>
          </div>
        </section>
      )}

      {/* Upgrade Banner */}
      {userTier === "free" && (
        <section className="py-6 px-6 bg-lws-amber/10 border-b border-lws-amber/20">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Lock className="w-5 h-5 text-lws-amber" />
              <p className="text-lws-ink">
                <strong>Module 1 is free to preview.</strong> Unlock all 8 modules with The Correspondence Method course.
              </p>
            </div>
            <a
              href="#membership"
              className="px-6 py-2 bg-lws-amber text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-rose transition-colors"
              data-testid="upgrade-cta"
            >
              View Membership
            </a>
          </div>
        </section>
      )}

      {userTier === "community" && (
        <section className="py-6 px-6 bg-lws-green/10 border-b border-lws-green/20">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BookOpen className="w-5 h-5 text-lws-green" />
              <p className="text-lws-ink">
                <strong>Community Member:</strong> You have access to Module 1. Purchase the full course to unlock Modules 2-8.
              </p>
            </div>
            <a
              href="#membership"
              className="px-6 py-2 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
              data-testid="upgrade-cta"
            >
              Get Full Course
            </a>
          </div>
        </section>
      )}

      {/* Modules List */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {modules.map((module) => (
            <ModuleAccordion
              key={module.id}
              module={module}
              isOpen={openModule === module.id}
              onToggle={() => setOpenModule(openModule === module.id ? null : module.id)}
              onLessonComplete={handleLessonComplete}
              completedLessons={completedLessons}
              onLessonClick={(lesson) => handleLessonClick(lesson, module.number)}
            />
          ))}
        </div>
        
        {/* Resource Library Link */}
        {user && userTier !== "free" && (
          <div className="max-w-4xl mx-auto mt-8 p-6 bg-lws-green/5 border border-lws-green/20 rounded-lg text-center">
            <Library className="w-8 h-8 text-lws-green mx-auto mb-3" />
            <p className="text-lws-ink mb-3">
              Looking for downloadable resources, templates, and guides?
            </p>
            <a
              href="#resources"
              className="inline-flex items-center gap-2 px-6 py-2 bg-lws-green text-white rounded-lg hover:bg-lws-green/90 transition-colors"
              data-testid="resource-library-link"
            >
              <Download className="w-4 h-4" />
              Visit Resource Library
            </a>
          </div>
        )}
      </section>

      {/* What You'll Learn */}
      <section className="py-24 px-6 bg-lws-paper border-t border-lws-ink/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-serif text-center text-lws-ink mb-16">
            What You&apos;ll Master
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: "The Philosophy of Presence", desc: "Approach letter writing as a mindful practice" },
              { title: "Finding Your Voice", desc: "Know what to write when you don&apos;t know what to write" },
              { title: "The Craft of Connection", desc: "Build deeper relationships through correspondence" },
              { title: "Tools & Materials", desc: "Curate your perfect writing toolkit" },
              { title: "Building Your Practice", desc: "Create sustainable letter writing habits" },
              { title: "The Art of Response", desc: "Read and respond with intention" },
              { title: "Community & Chapters", desc: "Connect with fellow letter writers" },
              { title: "The Lifelong Practice", desc: "Sustain your correspondence journey" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-8 h-8 bg-lws-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-lws-green" />
                </div>
                <div>
                  <h4 className="font-serif text-lws-ink">{item.title}</h4>
                  <p className="text-sm text-lws-taupe">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {!user && (
        <section className="py-24 px-6 bg-lws-green text-lws-paper text-center">
          <h2 className="text-3xl font-serif mb-6">Ready to begin?</h2>
          <p className="text-lws-paper/80 mb-8 max-w-lg mx-auto">
            Create a free account to track your progress through Module 1.
          </p>
          <a
            href="#register/course"
            className="inline-flex items-center px-8 py-4 bg-lws-paper text-lws-green text-xs uppercase tracking-wider hover:bg-lws-amber hover:text-lws-paper transition-colors"
            data-testid="courses-register-cta"
          >
            Create Free Account <ChevronRight className="w-4 h-4 ml-2" />
          </a>
        </section>
      )}

      {/* Video Player Modal */}
      <AnimatePresence>
        {selectedLesson && selectedLesson.type === 'video' && (
          <VideoPlayerModal
            lesson={selectedLesson}
            onClose={() => setSelectedLesson(null)}
          />
        )}
      </AnimatePresence>

      {/* Resource Download Modal */}
      <AnimatePresence>
        {selectedLesson && selectedLesson.type !== 'video' && selectedLesson.downloadable && (
          <ResourceModal
            lesson={selectedLesson}
            onClose={() => setSelectedLesson(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CoursesPage;
